import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Toast from '../components/common/Toast';

type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastContextType {
  showToast: (
    type: ToastType,
    message: string,
    options?: { duration?: number }
  ) => string;
}

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * ToastProvider renders a portal-based toast stack with auto-dismiss support.
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Record<string, number>>({});

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));

    if (timers.current[id]) {
      window.clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, options?: { duration?: number }) => {
      const id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      const duration = options?.duration ?? 4500;

      setToasts((prev) => [...prev, { id, type, message, duration }]);

      timers.current[id] = window.setTimeout(() => removeToast(id), duration);

      return id;
    },
    [removeToast]
  );

  // Clean up any remaining timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timers.current).forEach((t) => window.clearTimeout(t));
      timers.current = {};
    };
  }, []);

  const value = useMemo(
    () => ({
      showToast,
    }),
    [showToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast portal */}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            data-testid="toast-portal"
            className="fixed inset-0 pointer-events-none z-[60] flex flex-col items-end gap-3 px-4 py-6 sm:p-6"
          >
            {toasts.map((toast) => (
              <Toast
                key={toast.id}
                type={toast.type}
                message={toast.message}
                onClose={() => removeToast(toast.id)}
              />
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
};
